import * as React from 'react';
import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useAppContext } from '../context/AppContext';
import { Category } from '../types/category';
import { MarkerData, ExtendedMarkerData, MarkerFormData } from '../types/marker';
import { DEFAULT_MARKER_COLOR } from '../config/markerColors';
import { DEFAULT_UNIFIED_ICON } from '../config/unifiedIcons';
import { getFontAwesomeNameForCategory } from '../utils/iconMigration';
import { createCustomMarkerElement, UserAvatarInfo } from '../utils/markerUtils';
import { generateUserInitials } from '../utils/userUtils';
import { createRoot } from 'react-dom/client';
import { POPUP_OPTIONS } from '../config/mapConfig';
import '../styles/popup.css';
import { useMarkerActions } from './useMarkerActions';
import { isEqual } from '../utils/isEqual';
import useWindowSize from './useWindowSize';
import { markersService } from '../services/supabase';
import { usersService } from '../services/supabase/users.service';
import logger from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';
import { safeRef, withRef, doWithRef } from '../utils/refUtils';
import { eventsService } from '../services/events.service';
import { useUserFilter } from '../context/UserFilterContext';
import { User } from '../types/user';
import { supabase } from '../config/supabase';
import { useNotifications } from '../context/NotificationContext';

interface MarkerRefs {
  [key: string]: mapboxgl.Marker;
}

interface PopupRefs {
  [key: string]: ReturnType<typeof createRoot>;
}

export const useMarkers = (map: React.MutableRefObject<mapboxgl.Map | null>) => {
  const { state, dispatch } = useAppContext();
  const { createInitialAction: _createInitialAction, createUpdateAction: _createUpdateAction, mergeActions, createCategoryChangeAction } = useMarkerActions();
  const markerElementsRef = useRef<MarkerRefs>({});
  const lastOpenPopupRef = useRef<mapboxgl.Marker | null>(null);
  const popupRootRef = useRef<PopupRefs>({});
  const mapLoadedRef = useRef<boolean>(false);
  const isMovingRef = useRef<boolean>(false);
  const isInitialLoadDone = useRef<boolean>(false);
  const { selectedUserId } = useUserFilter(); // Utiliser le contexte de filtrage d'utilisateur
  const { state: notificationState } = useNotifications(); // Accéder aux notifications

  // État pour le modal de modification
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedMarker, setSelectedMarker] = useState<ExtendedMarkerData | null>(null);

  // État pour suivre si la carte est prête
  const [isMapReady, setIsMapReady] = useState<boolean>(false);
  
  // État pour stocker les informations des utilisateurs
  const [usersInfo, setUsersInfo] = useState<{[key: string]: UserAvatarInfo}>({});
  
  // État pour savoir s'il y a plusieurs utilisateurs dans l'agence
  const [hasMultipleUsers, setHasMultipleUsers] = useState<boolean>(false);
  
  // Vérifier si l'appareil est mobile (pour désactiver certaines fonctionnalités)
  const { isMobile } = useWindowSize();
  
  // Charger les informations des utilisateurs
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data, error } = await usersService.getUsersByAgency();
        
        if (error) {
          throw error;
        }
        
        if (data) {
          const usersMap: {[key: string]: UserAvatarInfo} = {};
          
          data.forEach((user: User) => {
            // Utiliser la fonction utilitaire pour générer les initiales
            const initials = generateUserInitials(user);
                
            usersMap[user.id] = {
              id: user.id,
              initials,
              color: user.color || undefined,
              photo_url: user.photo_url || undefined
            };
          });
          
          setUsersInfo(usersMap);
          
          // Déterminer s'il y a plusieurs utilisateurs dans l'agence
          setHasMultipleUsers(data.length > 1);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
      }
    };
    
    fetchUsers();
  }, []);
  
  // Effet pour rafraîchir les marqueurs lorsque le filtre utilisateur change
  useEffect(() => {
    // Forcer le rafraîchissement des marqueurs lorsque le filtre est défini sur "Tous les utilisateurs"
    if (map.current && mapLoadedRef.current) {
      logger.info('Rafraîchissement des marqueurs pour afficher les avatars utilisateurs');
      
      // Utiliser un événement pour forcer le rafraîchissement des marqueurs
      eventsService.emit('REFRESH_MAP_MARKERS', {});
    }
  }, [selectedUserId]);

  // Fonction pour valider un UUID
  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  // Mémoriser les marqueurs dans une Map pour un accès plus rapide
  const markersMap = useMemo(() => {
    const map: { [key: string]: ExtendedMarkerData } = {};
    state.markers.forEach(marker => {
      map[marker.id] = marker;
      // Debug supprimé pour améliorer les performances
      // Décommenter uniquement si nécessaire pour debug imageUrl
      // console.log('🖼️ DEBUG markersMap - Ajout marker:', {
      //   markerId: marker.id,
      //   hasImageUrl: !!marker.imageUrl,
      //   imageUrlLength: marker.imageUrl?.length || 0,
      //   markerKeys: Object.keys(marker)
      // });
    });
    return map;
  }, [state.markers]);

  // Mémoriser la map des catégories pour un accès plus rapide
  const categoriesMap = useMemo(() => {
    const map: { [key: string]: Category } = {};
    state.categories.forEach(category => {
      if (category && category.id) {
        // Validation des propriétés essentielles
        if (!category.name) {
          console.warn('Catégorie sans nom détectée:', category.id);
          return;
        }
        map[category.id] = {
          ...category,
          // Valeurs par défaut si non définies
          color: category.color || DEFAULT_MARKER_COLOR,
          icon: category.icon || DEFAULT_UNIFIED_ICON.faName
        };
      }
    });
    return map;
  }, [state.categories]);

  // Mémoriser les IDs des marqueurs par catégorie pour un accès plus rapide
  const markersByCategoryId = useMemo(() => {
    const categoryMarkers: { [categoryId: string]: Set<string> } = {};
    
    Object.values(markersMap).forEach(marker => {
      if (marker.categoryId) {
        if (!categoryMarkers[marker.categoryId]) {
          categoryMarkers[marker.categoryId] = new Set<string>();
        }
        categoryMarkers[marker.categoryId].add(marker.id);
      }
    });

    return categoryMarkers;
  }, [markersMap]);

  // Fonction pour calculer les marqueurs visibles
  const calculateVisibleMarkers = useCallback(() => {
    // Vérification des categoryId
    const validCategoryIds = new Set(state.categories.map(cat => cat.id));
    const invalidMarkers = Object.values(markersMap).filter(marker => {
      if (!marker.categoryId) {
        logger.error('Marqueur sans categoryId:', { markerId: marker.id, marker });
        return true;
      }
      if (!isValidUUID(marker.categoryId)) {
        logger.error('Marqueur avec categoryId invalide (pas un UUID):', { 
          markerId: marker.id, 
          categoryId: marker.categoryId,
          marker 
        });
        return true;
      }
      if (!validCategoryIds.has(marker.categoryId)) {
        logger.error('Marqueur avec categoryId inexistant:', { 
          markerId: marker.id, 
          categoryId: marker.categoryId,
          availableCategories: Array.from(validCategoryIds),
          marker 
        });
        return true;
      }
      return false;
    });

    if (invalidMarkers.length > 0) {
      logger.warn(`${invalidMarkers.length} marqueurs avec des categoryId invalides détectés`);
    }
    

    // Utiliser state.visibleCategories au lieu de isVisible
    const visibleCategories = state.categories.filter(cat => 
      state.visibleCategories.has(cat.id)
    );

    const visibleMarkersById: { [key: string]: ExtendedMarkerData[] } = {};
    let totalMarkers = 0;

    // Initialiser les tableaux pour chaque catégorie visible
    visibleCategories.forEach(category => {
      visibleMarkersById[category.id] = [];
    });

    // Grouper les marqueurs par catégorie
    Object.values(markersMap).forEach(marker => {
      // Ne traiter que les marqueurs avec des categoryId valides
      if (marker.categoryId && validCategoryIds.has(marker.categoryId)) {
        // Vérifier si la catégorie est visible
        if (state.visibleCategories.has(marker.categoryId)) {
          // Vérifier si le marqueur correspond au filtre utilisateur
          // Cette logique a été modifiée pour résoudre le problème des marqueurs qui n'apparaissaient pas
          // dans le bon filtre utilisateur, notamment les marqueurs sans user_id
          const matchesUserFilter = 
            // Cas 1: Pas de filtre utilisateur (afficher tous les marqueurs)
            selectedUserId === null ||
            // Cas 2: Filtre utilisateur spécifique (n'afficher que les marqueurs de cet utilisateur)
            // Note: Si le marqueur n'a pas de user_id, on le considère comme appartenant à tous les utilisateurs
            // pour éviter qu'il ne soit invisible
            (selectedUserId && (
              // Le marqueur appartient à l'utilisateur sélectionné
              marker.user_id === selectedUserId ||
              // Si le marqueur n'a pas de user_id, l'afficher pour tous les utilisateurs
              // Cette condition est essentielle pour les marqueurs créés avant la mise à jour
              marker.user_id === undefined || marker.user_id === null
            ));
          
          // Ne pas ajouter le marqueur s'il ne correspond pas au filtre utilisateur
          if (!matchesUserFilter) {
            // Log en mode development uniquement
            if (process.env.NODE_ENV === 'development') {
              logger.debug('Marqueur filtré:', {
                markerId: marker.id,
                markerUserId: marker.user_id,
                selectedUserId
              });
            }
            return; // Passer au marqueur suivant
          }
          
          // Filtrer par notifications si le filtre est activé
          if (state.showOnlyMarkersWithNotifications) {
            // Récupérer toutes les notifications pour ce marqueur
            const markerNotifications = notificationState.notifications.filter(
              notif => notif.markerId === marker.id
            );
            
            // Filtrer les notifications "non lues" selon la même logique que NotificationBell:
            // - Notifications non lues qui ne sont PAS des reminders
            // - OU reminders non lus dont la date est déjà échue (passée)
            const now = new Date();
            const hasUnreadNotifications = markerNotifications.some(notif => {
              if (notif.status !== 'unread') {
                return false; // Ignorer les notifications déjà lues
              }
              
              // Si c'est un reminder, vérifier si la date est échue
              if (notif.type === 'reminder' && notif.reminderDate) {
                const reminderDate = notif.reminderDate instanceof Date 
                  ? notif.reminderDate 
                  : new Date(notif.reminderDate);
                // Inclure seulement si la date de rappel est passée (échue)
                return reminderDate <= now;
              }
              
              // Pour les autres types de notifications, inclure si non lues
              return notif.type !== 'reminder';
            });
            
            if (!hasUnreadNotifications) {
              return; // Passer au marqueur suivant si pas de notifications
            }
          }
          
          // Ajouter le marqueur à la liste des marqueurs visibles pour cette catégorie
          if (!visibleMarkersById[marker.categoryId]) {
            visibleMarkersById[marker.categoryId] = [];
          }
          visibleMarkersById[marker.categoryId].push(marker);
          totalMarkers++;
        }
      }
    });

    const visibleMarkerIds = new Set<string>();

    // N'ajouter que les marqueurs des catégories visibles
    visibleCategories.forEach(category => {
      const markers = visibleMarkersById[category.id] || [];
      markers.forEach(marker => visibleMarkerIds.add(marker.id));
    });

    const result = {
      visibleMarkerIds: Array.from(visibleMarkerIds),
      resultCount: visibleMarkerIds.size
    };
    
    // Analyser les marqueurs visibles pour le débogage
    if (result.visibleMarkerIds.length > 0) {
      // Compter les marqueurs visibles par user_id
      const visibleMarkers = result.visibleMarkerIds.map(id => markersMap[id]).filter(m => m !== undefined);
      const userIds = visibleMarkers.map(m => m.user_id).filter(id => id !== null && id !== undefined) as string[];
      
      // Utiliser une approche compatible avec les versions plus anciennes de JavaScript
      const uniqueUserIdsSet = new Set<string>();
      userIds.forEach(id => uniqueUserIdsSet.add(id));
      // const uniqueUserIds = Array.from(uniqueUserIdsSet); // Unused for now
    }

    logger.debug('Résultat du calcul des marqueurs visibles:', {
      ...result,
      totalMarkers,
      visibleCategories: visibleCategories.map(cat => cat.name),
      markersByCategory: Object.entries(visibleMarkersById).map(([catId, markers]) => ({
        categoryId: catId,
        categoryName: categoriesMap[catId]?.name,
        markerCount: markers.length
      }))
    });

    return result;
  }, [state.categories, state.visibleCategories, state.showOnlyMarkersWithNotifications, markersMap, categoriesMap, selectedUserId, notificationState.notifications]);

  // Fonction pour calculer les marqueurs visibles
  const updateMarkerVisibility = useCallback((marker: mapboxgl.Marker, shouldBeVisible: boolean) => {
    const element = marker.getElement();
    const isCurrentlyVisible = element.style.display !== 'none';
    
    if (shouldBeVisible !== isCurrentlyVisible) {
      element.style.display = shouldBeVisible ? 'block' : 'none';
      logger.debug(`${shouldBeVisible ? 'Affichage' : 'Masquage'} du marker ${element.id}`, {
        markerId: element.id,
        shouldBeVisible,
        wasVisible: isCurrentlyVisible
      });
    }
  }, []);

  // Gestionnaire pour l'ouverture du modal de modification
  const handleManageMarker = useCallback((marker: MarkerData, notificationData?: { notificationId?: string, notificationTitle?: string, actionId?: string }) => {
    logger.debug('Gestion du marqueur:', {
      markerId: marker.id,
      address: marker.address,
      notificationData
    });
    
    // Log de l'état actuel des marqueurs
    logger.debug('État actuel des marqueurs:', {
      totalMarkersInState: state.markers.length,
      totalMarkersInMap: Object.keys(markersMap).length,
      markerIdsInState: state.markers.map(m => m.id),
      markerIdsInMap: Object.keys(markersMap)
    });
    
    // Fonction pour ouvrir le modal avec les données du marqueur
    const openModalWithMarkerData = (markerData: ExtendedMarkerData) => {
      setSelectedMarker({
        ...markerData,
        // Ajouter les données de notification si présentes
        notificationId: notificationData?.notificationId,
        notificationTitle: notificationData?.notificationTitle,
        _actionId: notificationData?.actionId
      });
      setIsEditModalOpen(true);
      logger.debug('Modal ouvert avec les données du marqueur:', {
        ...markerData,
        notificationId: notificationData?.notificationId,
        notificationTitle: notificationData?.notificationTitle,
        _actionId: notificationData?.actionId
      });
    };
    
    // S'assurer d'avoir les données complètes du marker
    let fullMarkerData = markersMap[marker.id];
    
    // Si les données ne sont pas dans markersMap, chercher directement dans state.markers
    if (!fullMarkerData) {
      logger.debug('Marqueur non trouvé dans markersMap, recherche dans state.markers');
      const foundMarker = state.markers.find(m => m.id === marker.id);
      
      if (foundMarker) {
        fullMarkerData = foundMarker;
        logger.debug('Marqueur trouvé dans state.markers:', fullMarkerData);
        openModalWithMarkerData(fullMarkerData);
        return;
      }
      
      // Si toujours pas trouvé, récupérer directement depuis Supabase
      logger.debug('Marqueur non trouvé dans state.markers, récupération depuis Supabase');
      
      // Utilisation d'une IIFE pour pouvoir utiliser async/await dans un useCallback
      (async () => {
        try {
          const markerFromDB = await markersService.getById(marker.id);
          if (markerFromDB) {
            logger.debug('Marqueur récupéré depuis Supabase:', markerFromDB);
            
            // Mettre à jour l'état avec le marqueur récupéré
            dispatch({ type: 'ADD_MARKER', payload: markerFromDB });
            
            // Ouvrir le modal avec les données récupérées
            openModalWithMarkerData(markerFromDB);
          } else {
            logger.error('Marqueur introuvable dans Supabase');
          }
        } catch (error) {
          logger.error('Erreur lors de la récupération du marqueur depuis Supabase:', error);
        }
      })();
      
      return;
    }
    
    // Si le marqueur est trouvé dans markersMap, ouvrir le modal
    openModalWithMarkerData(fullMarkerData);
  }, [markersMap, state.markers, dispatch]);

  // Mémoriser la fonction de création du contenu du popup
  const createPopupContent = useCallback((markerData: MarkerData, category?: Category) => {
    // Récupérer les données complètes depuis state.markers (plus à jour que markersMap)
    const fullMarkerData = state.markers.find(m => m.id === markerData.id) || markersMap[markerData.id];
    
    console.log('🖼️ DEBUG Popup - imageUrl:', {
      markerId: markerData.id,
      hasBasicImageUrl: !!(markerData as any).imageUrl,
      hasFullImageUrl: !!fullMarkerData?.imageUrl,
      fullImageUrlLength: fullMarkerData?.imageUrl?.length || 0,
      fullImageUrlPrefix: fullMarkerData?.imageUrl?.substring(0, 50) || 'undefined',
      markerDataKeys: Object.keys(markerData),
      fullMarkerDataKeys: fullMarkerData ? Object.keys(fullMarkerData) : [],
      timestamp: new Date().toISOString()
    });

    const elements = [
      React.createElement('div', { className: 'popup-header' },
        React.createElement('h3', { className: 'popup-title' }, category?.name || 'Marqueur'),
        React.createElement('p', { className: 'popup-address' }, markerData.address)
      )
    ];

    // Utiliser fullMarkerData pour accéder à imageUrl
    if (fullMarkerData?.imageUrl) {
      elements.push(
        React.createElement('div', { className: 'popup-image-container' },
          React.createElement('img', {
            src: fullMarkerData.imageUrl,
            alt: 'Photo du bien',
            className: 'popup-image',
            onError: (e: any) => {
              e.target.style.display = 'none';
            }
          })
        )
      );
    }

    // Ajouter les notifications futures non lues (unread)
    if (fullMarkerData?.actions) {
      const now = new Date();
      
      // Filtrer les actions avec rappels futurs ET notifications non lues
      const futureUnreadReminders = fullMarkerData.actions.filter(action => {
        if (!action.reminderDate || action.status === 'completed') {
          return false;
        }
        
        const reminderDate = new Date(action.reminderDate);
        if (reminderDate <= now) {
          return false;
        }
        
        // Vérifier si la notification liée est non lue
        const relatedNotification = notificationState.notifications.find(
          notif => notif.actionId === action.id
        );
        
        return relatedNotification?.status === 'unread';
      });

      console.log('🔔 DEBUG Popup - Notifications:', {
        markerId: markerData.id,
        totalActions: fullMarkerData.actions.length,
        futureUnreadReminders: futureUnreadReminders.length,
        timestamp: new Date().toISOString()
      });

      if (futureUnreadReminders.length > 0) {
        const notificationElements = [
          React.createElement('div', { className: 'popup-notifications-title' }, 'Rappels à venir')
        ];

        futureUnreadReminders.forEach(action => {
          const reminderDate = new Date(action.reminderDate!);
          const dateStr = reminderDate.toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });

          // Formater le type d'action
          const formatActionType = (type: string): string => {
            switch (type) {
              case 'call': return 'Sonner';
              case 'visit': return 'Visite';
              case 'email': return 'Email';
              case 'note': return 'Note';
              case 'update': return 'Mise à jour';
              case 'create': return 'Création';
              case 'category_change': return 'Changement de catégorie';
              default: return type.charAt(0).toUpperCase() + type.slice(1);
            }
          };

          const actionTypeText = formatActionType(action.type);
          const displayText = `${dateStr} - ${actionTypeText}: ${action.description}`;

          notificationElements.push(
            React.createElement('div', { 
              className: 'popup-notification-item',
              key: action.id 
            },
              // Cercle rouge avec check blanc (comme dans le modal)
              React.createElement('div', {
                className: 'popup-notification-badge',
                style: {
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#dc3545',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }
              },
                React.createElement('svg', {
                  width: '8',
                  height: '8',
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'white',
                  strokeWidth: '3',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round'
                },
                  React.createElement('polyline', { points: '20 6 9 17 4 12' })
                )
              ),
              React.createElement('div', { className: 'popup-notification-text' }, displayText)
            )
          );
        });

        elements.push(
          React.createElement('div', { className: 'popup-notifications' }, ...notificationElements)
        );
      }
    }

    elements.push(
      React.createElement('div', { className: 'popup-actions' },
        React.createElement('button', {
          className: 'popup-manage-btn',
          onClick: () => handleManageMarker(markerData)
        }, 'Gérer')
      )
    );

    return React.createElement('div', { className: 'popup-content' }, ...elements);
  }, [handleManageMarker, markersMap, notificationState.notifications]);

  // Mémoriser la fonction de création du popup
  const createPopupForMarker = useCallback((markerData: MarkerData, category?: Category) => {
    const popupDiv = document.createElement('div');
    const root = createRoot(popupDiv);
    
    // Stocker la référence au root du popup
    doWithRef(popupRootRef, (popupRoots) => {
      popupRoots[markerData.id] = root;
    });
    
    root.render(createPopupContent(markerData, category));
    return popupDiv;
  }, [createPopupContent]);

  // Gérer le clic sur un marker
  const handleMarkerClick = useCallback(async (
    e: MouseEvent,
    marker: mapboxgl.Marker,
    markerData: MarkerData,
    category?: Category
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Vérifier si la carte est initialisée
    if (!safeRef(map)) return;

    // Récupérer le popup ouvert actuellement
    const lastOpenPopup = safeRef(lastOpenPopupRef);
    
    logger.debug('Click sur le marqueur:', {
      markerId: markerData.id,
      hasPopup: !!marker.getPopup(),
      lastOpenPopup: lastOpenPopup?.getElement()?.id
    });

    // Si on clique sur le même marqueur qui a déjà un popup ouvert, on le ferme
    if (lastOpenPopup === marker) {
      logger.debug('Fermeture du popup actuel:', markerData.id);
      marker.getPopup()?.remove();
      lastOpenPopupRef.current = null;
      return;
    }

    // Fermer le popup précédent s'il existe
    withRef(lastOpenPopupRef, (popup) => {
      logger.debug('Fermeture du popup précédent:', popup.getElement()?.id);
      popup.getPopup()?.remove();
      lastOpenPopupRef.current = null;
    });

    // Supprimer l'ancien popup du marker s'il existe
    const oldPopup = marker.getPopup();
    if (oldPopup) {
      logger.debug('Suppression de l\'ancien popup du marker:', markerData.id);
      oldPopup.remove();
      marker.setPopup(null as any);
    }

    // Recharger les données fraîches du marker depuis la base de données
    logger.debug('Rechargement des données du marker depuis la base:', markerData.id);
    const freshMarkerData = state.markers.find(m => m.id === markerData.id) || markerData;
    
    // Créer un nouveau popup avec les données fraîches
    logger.debug('Création d\'un nouveau popup pour:', markerData.id);
    const popupContent = createPopupForMarker(freshMarkerData, category);
    const popup = new mapboxgl.Popup({
      ...POPUP_OPTIONS,
      closeButton: true,
      closeOnClick: false,
      maxWidth: '300px',
      offset: 25,
      focusAfterOpen: false
    }).setDOMContent(popupContent);

    // Ajouter un écouteur d'événement sur le popup
    popup.on('close', () => {
      logger.debug('Popup fermé pour:', markerData.id);
      // Vérifier si le marqueur actuel est celui qui était ouvert
      const currentOpenPopup = safeRef(lastOpenPopupRef);
      if (currentOpenPopup === marker) {
        // Réinitialiser la référence
        lastOpenPopupRef.current = null;
      }
    });

    // Attacher le nouveau popup au marker
    marker.setPopup(popup);

    // Ouvrir le popup du marqueur actuel
    logger.debug('Ouverture du popup pour:', markerData.id);
    
    // Ajouter le popup à la carte si elle est initialisée
    doWithRef(map, (mapInstance) => {
      if (popup) {
        popup.addTo(mapInstance);
        lastOpenPopupRef.current = marker;
      }
    });
  }, [map, createPopupForMarker, state.markers]);

  // Note: Le popup est recréé à chaque clic avec les données fraîches depuis state.markers
  // Il n'est donc pas nécessaire de le rafraîchir en temps réel

  // Fonction pour créer ou mettre à jour un marqueur sur la carte
  const createOrUpdateMarker = useCallback((markerData: MarkerData, category?: Category) => {
    // Vérifier si la carte est initialisée
    if (!safeRef(map)) {
      logger.error('Carte non initialisée lors de la création/mise à jour du marker:', markerData.id);
      return;
    }

    logger.debug('Création/Mise à jour du marqueur:', {
      id: markerData.id,
      address: markerData.address,
      category: category?.name,
      categoryId: markerData.categoryId,
      shouldBeVisible: state.visibleCategories.has(markerData.categoryId || ''),
      visibleCategories: Array.from(state.visibleCategories),
      coordinates: markerData.coordinates,
      mapInitialized: !!safeRef(map),
      mapLoaded: safeRef(mapLoadedRef, false)
    });

    const markerColor = category?.color || DEFAULT_MARKER_COLOR;
    const iconName = category?.icon ? getFontAwesomeNameForCategory(category.icon) : DEFAULT_UNIFIED_ICON.faName;
    const markerShape = category?.markerShape || 'circle';

    // Nettoyer l'ancien marker s'il existe
    doWithRef(markerElementsRef, (elements) => {
      if (elements[markerData.id]) {
        logger.debug('Nettoyage de l\'ancien marker:', markerData.id);
        const existingMarker = elements[markerData.id];
        existingMarker.remove(); // Retirer complètement l'ancien marker
        delete elements[markerData.id];
      }
    });

    // Créer un nouveau marker
    try {
      const element = document.createElement('div');
      element.className = 'marker';
      element.id = markerData.id;
      
      // Déterminer si nous devons afficher l'avatar de l'utilisateur
      // Afficher l'avatar uniquement si :
      // 1. Le mode "Tous les utilisateurs" est activé (selectedUserId === null)
      // 2. Il y a plusieurs utilisateurs dans l'agence (hasMultipleUsers === true)
      // 3. Le marqueur a un user_id associé
      const showUserAvatar = selectedUserId === null && hasMultipleUsers && !!markerData.user_id;
      
      // Récupérer les informations de l'utilisateur si disponibles
      const userInfo = markerData.user_id ? usersInfo[markerData.user_id] : undefined;
      
      console.log('🚩 Création du marker avec user_id:', { 
        markerId: markerData.id, 
        user_id: markerData.user_id, 
        showUserAvatar, 
        hasUserInfo: !!userInfo,
        selectedUserId,
        hasMultipleUsers,
        usersInfoCount: Object.keys(usersInfo).length
      });
      
      if (markerData.user_id && !userInfo) {
        console.warn('⚠️ Marker avec user_id mais pas d\'infos utilisateur:', {
          markerId: markerData.id,
          user_id: markerData.user_id,
          availableUserIds: Object.keys(usersInfo)
        });
      }
      
      // Créer l'élément du marqueur avec ou sans avatar d'utilisateur
      element.appendChild(createCustomMarkerElement(markerColor, iconName, userInfo, showUserAvatar, markerData.id, markerShape));

      // Vérifier que les coordonnées sont valides
      if (!markerData.coordinates || markerData.coordinates.length !== 2 || 
          isNaN(markerData.coordinates[0]) || isNaN(markerData.coordinates[1])) {
        logger.error(`Coordonnées invalides pour le marqueur ${markerData.id}:`, markerData.coordinates);
        return;
      }

      logger.debug(`Création du marker Mapbox pour ${markerData.id} aux coordonnées [${markerData.coordinates[0]}, ${markerData.coordinates[1]}]`);
      
      // Créer le marker Mapbox
      const marker = new mapboxgl.Marker({
        element,
        draggable: false
      }).setLngLat(markerData.coordinates);
      
      // Ajouter le marker à la carte
      doWithRef(map, (mapInstance) => {
        marker.addTo(mapInstance);
      });

      // Vérifier que le marker a bien été ajouté à la carte
      logger.debug(`Vérification du marker ${markerData.id} après addTo:`, {
        hasElement: !!marker.getElement(),
        elementId: marker.getElement().id,
        isOnMap: !!marker.getElement().parentNode,
        coordinates: marker.getLngLat()
      });

      // Définir la visibilité initiale
      requestAnimationFrame(() => {
        updateMarkerVisibility(marker, state.visibleCategories.has(markerData.categoryId || ''));
        
        // Vérifier la visibilité après mise à jour
        const element = marker.getElement();
        logger.debug(`Visibilité du marker ${markerData.id} après updateMarkerVisibility:`, {
          display: element.style.display,
          isVisible: element.style.display !== 'none',
          shouldBeVisible: state.visibleCategories.has(markerData.categoryId || '')
        });
      });

      // Gérer les événements du marker
      const handleClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleMarkerClick(e, marker, markerData, category);
      };

      marker.getElement().addEventListener('click', handleClick);
      
      // Stocker les références
      doWithRef(markerElementsRef, (elements) => {
        elements[markerData.id] = marker;
      });

      logger.debug('Nouveau marker créé sur la carte:', {
        id: markerData.id,
        isVisible: state.visibleCategories.has(markerData.categoryId || ''),
        hasElement: !!marker.getElement(),
        totalMarkers: Object.keys(markerElementsRef.current || {}).length
      });
    } catch (error) {
      logger.error('Erreur lors de la création du marker:', {
        error,
        markerId: markerData.id,
        categoryId: markerData.categoryId
      });
    }
  }, [map, state.visibleCategories, updateMarkerVisibility, handleMarkerClick]);

  // Fonction pour créer un nouveau marqueur
  const createMarker = useCallback(async (markerData: MarkerFormData): Promise<ExtendedMarkerData> => {
    try {
      logger.debug('Création d\'un nouveau marqueur:', markerData);
      
      // Générer un ID unique pour le nouveau marqueur
      const newMarkerId = uuidv4();
      
      // Récupérer l'utilisateur actuel depuis Supabase
      const { data: authData } = await supabase.auth.getSession();
      const currentUser = authData.session?.user;
      
      // Préparer les données du nouveau marqueur en respectant le type MarkerFormData
      const newMarker: MarkerFormData & { id?: string, user_id?: string } = {
        ...markerData,
        coordinates: markerData.coordinates,
        address: markerData.address,
        categoryId: markerData.categoryId || null,
        name: markerData.name || '',
        description: markerData.description || '',
        contact_address: markerData.contact_address || '',
        phone: markerData.phone || '',
        email: markerData.email || '',
        notes: markerData.notes || '',
        // Ajouter explicitement l'ID de l'utilisateur actuel
        user_id: currentUser?.id
      };
      
      // Log en mode development uniquement
      if (process.env.NODE_ENV === 'development') {
        logger.debug('Création d\'un nouveau marqueur avec user_id:', {
          newMarkerId,
          userId: currentUser?.id,
          selectedUserId
        });
      }
      
      // Ajouter l'ID généré (non inclus dans MarkerFormData)
      newMarker.id = newMarkerId;
      
      // S'assurer que les actions ont un markerId correct si elles existent
      if (newMarker.actions && newMarker.actions.length > 0) {
        newMarker.actions = newMarker.actions.map(action => ({
          ...action,
          markerId: newMarkerId,
          // Ajouter également l'ID de l'utilisateur aux actions
          user_id: currentUser?.id
        })) as any; // Cast temporaire pour résoudre le problème de type
      }
      
      // Créer le marqueur dans Supabase avec toutes les données
      const createdMarker = await markersService.create(newMarker);
      
      logger.debug('Nouveau marker créé dans Supabase:', {
        id: createdMarker.id,
        coordinates: createdMarker.coordinates,
        address: createdMarker.address,
        categoryId: createdMarker.categoryId,
        actions: createdMarker.actions?.length || 0
      });

      // Mettre à jour l'état avec le nouveau marker
      dispatch({ type: 'ADD_MARKER', payload: createdMarker });
      
      logger.debug('Marker ajouté à l\'état, vérification:', {
        markerInState: !!state.markers.find(m => m.id === createdMarker.id),
        stateMarkerIds: state.markers.map(m => m.id)
      });

      // Forcer la mise à jour des catégories visibles
      if (createdMarker.categoryId) {
        const updatedVisibleCategories = new Set(state.visibleCategories);
        updatedVisibleCategories.add(createdMarker.categoryId);
        dispatch({ type: 'SET_VISIBLE_CATEGORIES', payload: updatedVisibleCategories });
      }

      // Forcer la création/mise à jour immédiate du marker sur la carte
      if (createdMarker.categoryId) {
        const category = categoriesMap[createdMarker.categoryId];
        if (category) {
          // Création optimisée avec un seul appel à createOrUpdateMarker
          // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
          requestAnimationFrame(() => {
            createOrUpdateMarker(createdMarker, category);
            
            // Log de vérification après la mise à jour
            logger.debug('État après création du marker:', {
              markerExists: !!safeRef(markerElementsRef)?.[createdMarker.id],
              categoryVisible: state.visibleCategories.has(createdMarker.categoryId || ''),
              markerCategory: category,
              visibleCategories: Array.from(state.visibleCategories),
              totalMarkers: Object.keys(markerElementsRef.current || {}).length
            });
            
            // Forcer un rafraîchissement complet des marqueurs pour s'assurer que le nouveau marqueur est visible
            // avec le bon filtre utilisateur
            if (process.env.NODE_ENV === 'development') {
              logger.debug('Émission d\'un événement pour rafraîchir les marqueurs après création');
            }
            
            // Utiliser l'événement REFRESH_MAP_MARKERS pour forcer un rafraîchissement
            // Cette étape est cruciale pour que le nouveau marqueur apparaisse immédiatement
            eventsService.emit('REFRESH_MAP_MARKERS', {
              newMarkerId: createdMarker.id,
              timestamp: new Date().toISOString()
            });
          });
        }
      }

      return createdMarker;
    } catch (error) {
      logger.error('Erreur lors de la création du marqueur:', error);
      throw error;
    }
  }, [dispatch, state.visibleCategories, categoriesMap, createOrUpdateMarker, state.markers, markersMap]);

  // Mettre à jour un marqueur
  const updateMarker = useCallback(async (markerIdOrData: string | ExtendedMarkerData, markerData?: Partial<MarkerFormData>) => {
    try {
      let updatedMarker: ExtendedMarkerData;
      
      if (typeof markerIdOrData === 'string') {
        // Cas 1: Mise à jour via ID et données partielles
        if (!markerData) {
          throw new Error('markerData est requis lorsque markerIdOrData est un ID');
        }
        
        // Créer une copie des données pour éviter de modifier l'original
        const adaptedData: any = { ...markerData };
        
        // Traiter les actions si elles existent
        if (markerData.actions && markerData.actions.length > 0) {
          adaptedData.actions = markerData.actions.map(action => {
            // S'assurer que chaque action a un ID et un markerId
            const actionCopy: any = { ...action };
            
            // Vérifier si l'action a un ID
            if (!actionCopy.id) {
              actionCopy.id = `temp_${uuidv4()}`;
            }
            
            // S'assurer que markerId est défini
            actionCopy.markerId = markerIdOrData;
            
            // S'assurer que le type est compatible
            actionCopy.type = action.type;
            
            // S'assurer que le statut est défini
            if (!actionCopy.status) {
              actionCopy.status = 'pending';
            }
            
            return actionCopy;
          });
        }
        
        logger.debug('Mise à jour de la catégorie du marker:', {
          markerId: markerIdOrData,
          categoryId: markerData.categoryId
        });
        updatedMarker = await markersService.update(markerIdOrData, adaptedData);
      } else {
        // Cas 2: Mise à jour directe avec données complètes
        
        // Si la catégorie a changé, on le note dans les logs
        if (markerIdOrData.categoryId) {
          logger.debug('Mise à jour de la catégorie du marker:', {
            markerId: markerIdOrData.id,
            categoryId: markerIdOrData.categoryId
          });
          updatedMarker = await markersService.update(markerIdOrData.id, markerIdOrData);
        } else {
          updatedMarker = await markersService.update(markerIdOrData.id, markerIdOrData);
        }
      }
      
      // Mettre à jour l'état
      dispatch({ type: 'UPDATE_MARKER', payload: updatedMarker });

      // S'assurer que la catégorie est visible
      if (updatedMarker.categoryId && !state.visibleCategories.has(updatedMarker.categoryId)) {
        const updatedVisibleCategories = new Set(state.visibleCategories);
        updatedVisibleCategories.add(updatedMarker.categoryId);
        dispatch({ type: 'SET_VISIBLE_CATEGORIES', payload: updatedVisibleCategories });
      }

      // Mettre à jour l'apparence du marker sur la carte
      if (updatedMarker.categoryId) {
        const category = categoriesMap[updatedMarker.categoryId];
        if (category) {
          logger.debug('Mise à jour de l\'apparence du marker:', {
            markerId: updatedMarker.id,
            categoryId: updatedMarker.categoryId,
            categoryName: category.name
          });
          
          // Créer directement le nouveau marker sans délai pour éviter le clignotement
          createOrUpdateMarker(updatedMarker, category);
        }
      }

      // Émettre un événement pour informer les autres composants de la mise à jour
      eventsService.emit('MARKERS_UPDATED', {
        markerId: updatedMarker.id,
        categoryId: updatedMarker.categoryId
      });

      return updatedMarker;
    } catch (error) {
      logger.error('Erreur lors de la mise à jour du marqueur:', error);
      throw error;
    }
  }, [dispatch, state.visibleCategories, categoriesMap, createOrUpdateMarker]);

  // Supprimer un marqueur
  const deleteMarker = useCallback(async (markerId: string) => {
    try {
      await markersService.delete(markerId);
      dispatch({ type: 'DELETE_MARKER', payload: markerId });
    } catch (error) {
      logger.error('Erreur lors de la suppression du marqueur:', error);
      throw error;
    }
  }, [dispatch]);

  // Fonction pour calculer les marqueurs visibles
  const visibleMarkers = useMemo(() => {
    const { visibleMarkerIds } = calculateVisibleMarkers();
    
    const result = Array.from(visibleMarkerIds).map(id => markersMap[id]);
    return result;
  }, [calculateVisibleMarkers, markersMap]);

  // Fonction pour ouvrir le popup d'un marqueur spécifique
  const openMarkerPopup = useCallback((markerId: string) => {
    // Utiliser withRef pour accéder au marqueur de façon sécurisée
    let marker: mapboxgl.Marker | undefined;
    withRef(markerElementsRef, (elements) => {
      marker = elements[markerId];
    });
    
    const markerData = markersMap[markerId];
    const category = markerData?.categoryId ? categoriesMap[markerData.categoryId] : undefined;
    
    if (marker && markerData) {
      handleMarkerClick(new MouseEvent('click'), marker, markerData, category);
    }
  }, [markerElementsRef, markersMap, categoriesMap, handleMarkerClick]);

  // Fonction pour ajouter un marqueur
  const addMarker = useCallback(async (coordinates: [number, number], data: MarkerFormData): Promise<ExtendedMarkerData> => {
    // Vérifier si la carte est initialisée
    if (!safeRef(map)) {
      logger.error('Carte non initialisée lors de l\'ajout d\'un marqueur');
      throw new Error('Carte non initialisée');
    }

    logger.debug('Création d\'un nouveau marqueur:', {
      coordinates,
      data,
      availableCategories: Object.values(categoriesMap).map(c => ({
        id: c.id,
        name: c.name
      }))
    });

    // S'assurer que la catégorie existe
    if (data.categoryId && !categoriesMap[data.categoryId]) {
      const error = `Tentative de création d'un marqueur avec une catégorie invalide: ${data.categoryId}`;
      logger.error(error);
      throw new Error(error);
    }

    // Ajouter les coordonnées aux données du marker
    const markerData: MarkerFormData = {
      ...data,
      coordinates: coordinates
    };

    const newMarker = await createMarker(markerData);
    return newMarker;
  }, [map, categoriesMap, createMarker]);

  // Fonction pour fermer le modal de modification
  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedMarker(null);
  }, []);

  // Fonction pour sauvegarder les modifications d'un marqueur
  const handleSaveMarker = useCallback(async (markerData: ExtendedMarkerData): Promise<ExtendedMarkerData> => {
    // Vérifier si la carte est initialisée
    if (!safeRef(map)) {
      logger.error('Carte non initialisée lors de la sauvegarde du marker');
      throw new Error('Carte non initialisée');
    }

    try {
      logger.debug('Début de la sauvegarde du marker:', {
        id: markerData.id,
        categoryId: markerData.categoryId
      });

      // Fermer le popup s'il est ouvert
      withRef(markerElementsRef, (elements) => {
        const existingMarker = elements[markerData.id];
        if (existingMarker && existingMarker.getPopup()) {
          existingMarker.getPopup()?.remove();
        }
      });

      // Mettre à jour le marker
      const updatedMarker = await updateMarker(markerData);
      
      logger.debug('Marker mis à jour avec succès:', {
        id: updatedMarker.id,
        categoryId: updatedMarker.categoryId
      });

      // Fermer le modal
      setIsEditModalOpen(false);
      setSelectedMarker(null);

      // Retourner le marqueur mis à jour
      return updatedMarker;
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde du marker:', {
        error,
        markerId: markerData.id
      });
      throw error;
    }
  }, [map, updateMarker, markerElementsRef]);

  // Fonction pour sauvegarder les modifications d'un marqueur existant
  const _handleSaveEdit = useCallback(async (coordinates: [number, number], data: { address: string; categoryId: string | null }) => {
    if (!selectedMarker) return;
    
    try {
      // Vérifier si la catégorie a changé
      const oldCategory = selectedMarker.categoryId ? categoriesMap[selectedMarker.categoryId] : undefined;
      const newCategory = data.categoryId ? categoriesMap[data.categoryId] : undefined;
      
      const action = createCategoryChangeAction(oldCategory, newCategory);

      // Mettre à jour le marqueur avec la nouvelle catégorie et l'action
      dispatch({
        type: 'UPDATE_MARKER',
        payload: {
          ...selectedMarker,
          categoryId: data.categoryId || '',
          actions: mergeActions(selectedMarker.actions || [], action),
          updatedAt: new Date()
        }
      });

      // Mettre à jour le marker sur la carte
      const markerElement = markerElementsRef.current[selectedMarker.id];
      if (markerElement) {
        markerElement.setLngLat(coordinates);
        
        // Fermer et recréer le popup pour le mettre à jour
        const popup = popupRootRef.current[selectedMarker.id];
        if (popup) {
          // Supprimer l'ancien popup
          const popupContainer = document.getElementById(`popup-${selectedMarker.id}`);
          if (popupContainer) {
            popupContainer.remove();
          }
          delete popupRootRef.current[selectedMarker.id];
          
          // Créer un nouveau popup avec les données mises à jour
          const category = data.categoryId ? categoriesMap[data.categoryId] : undefined;
          createOrUpdateMarker(
            { ...selectedMarker, categoryId: data.categoryId },
            category
          );
        }
      }

      // Fermer le modal
      handleCloseEditModal();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du marker:', error);
    }
  }, [selectedMarker, dispatch, categoriesMap, createOrUpdateMarker, createCategoryChangeAction, mergeActions]);

  // Rafraîchir un seul marqueur sur la carte
  const refreshSingleMarker = useCallback((markerId: string, newCategoryId: string) => {
    if (!map.current) {
      logger.warn('Impossible de rafraîchir le marqueur: carte non initialisée');
      return;
    }

    try {
      // Trouver le marqueur dans l'état
      const markerToUpdate = state.markers.find(m => m.id === markerId);
      
      if (!markerToUpdate) {
        logger.warn(`Marqueur ${markerId} non trouvé dans l'état`);
        return;
      }

      // Vérifier que le marqueur a bien une catégorie
      if (!newCategoryId) {
        logger.error(`Tentative de mise à jour du marqueur ${markerId} avec une catégorie vide`);
        return;
      }

      // Supprimer uniquement ce marqueur de la carte
      const markerElement = markerElementsRef.current[markerId];
      if (markerElement) {
        markerElement.remove();
        delete markerElementsRef.current[markerId];
        logger.debug(`Marqueur ${markerId} supprimé de la carte`);
      }

      // Mettre à jour la catégorie du marqueur dans notre copie locale
      const updatedMarker = { 
        ...markerToUpdate, 
        categoryId: newCategoryId 
      };
      
      // Mettre à jour l'état global pour que la sidebar soit mise à jour
      dispatch({
        type: 'UPDATE_MARKER',
        payload: {
          id: markerId,
          data: updatedMarker
        }
      });
      
      // Recréer uniquement ce marqueur avec sa nouvelle catégorie
      const category = categoriesMap[newCategoryId];
      if (category) {
        createOrUpdateMarker(updatedMarker, category);
        logger.debug(`Marqueur ${markerId} recréé avec la catégorie ${category.name}`);
      } else {
        logger.warn(`Catégorie ${newCategoryId} non trouvée pour le marqueur ${markerId}`);
      }
    } catch (error) {
      logger.error(`Erreur lors du rafraîchissement du marqueur ${markerId}:`, error);
    }
  }, [map, state.markers, categoriesMap, createOrUpdateMarker, dispatch]);

  // Fonction pour nettoyer tous les marqueurs de la carte de manière optimisée
  const clearAllMarkersFromMap = useCallback(() => {
    logger.debug('Nettoyage de tous les marqueurs de la carte');
    
    if (!map.current) {
      logger.warn('Impossible de nettoyer les marqueurs: carte non initialisée');
      return;
    }
    
    try {
      // Optimisation: Utiliser Object.values pour éviter de créer un tableau intermédiaire
      const markerCount = Object.keys(markerElementsRef.current).length;
      logger.debug(`Tentative de suppression de ${markerCount} marqueurs`);
      
      // Supprimer les marqueurs par lots pour améliorer les performances
      // Éviter les logs excessifs qui peuvent ralentir le processus
      let removedCount = 0;
      
      // Utiliser Object.values pour itérer directement sur les marqueurs
      Object.entries(markerElementsRef.current).forEach(([markerId, marker]) => {
        try {
          marker.remove();
          delete markerElementsRef.current[markerId];
          removedCount++;
        } catch (err) {
          // Réduire la verbosité des logs en cas d'erreur
          if (removedCount % 10 === 0) { // Log seulement toutes les 10 erreurs
            logger.error(`Erreur lors de la suppression des marqueurs:`, err);
          }
        }
      });
      
      logger.debug(`${removedCount}/${markerCount} marqueurs supprimés avec succès`);
      
      // Optimisation: Utiliser une seule requête DOM et supprimer par lots
      if (map.current.getContainer()) {
        const mapContainer = map.current.getContainer();
        const markerElements = mapContainer.querySelectorAll('.mapboxgl-marker');
        
        if (markerElements.length > 0) {
          logger.debug(`Nettoyage de ${markerElements.length} éléments de marqueur restants dans le DOM`);
          
          // Utiliser un DocumentFragment pour optimiser les opérations DOM
          const _fragment = document.createDocumentFragment();
          markerElements.forEach(element => {
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
          });
        }
      }
      
      // Réinitialiser complètement les références
      markerElementsRef.current = {};
      popupRootRef.current = {};
      
      // Forcer le garbage collector à libérer la mémoire
      if (window.gc) {
        try {
          window.gc();
        } catch (e) {
          // Ignorer les erreurs si gc n'est pas disponible
        }
      }
      
      logger.info(`${markerCount} marqueurs ont été nettoyés de la carte`);
    } catch (error) {
      logger.error('Erreur lors du nettoyage des marqueurs:', error);
    }
  }, [map]);

  // Fonction pour rafraîchir tous les marqueurs sur la carte
  const refreshAllMarkers = useCallback((excludedCategoryIds: string[] = []) => {
    logger.debug('Rafraîchissement optimisé des marqueurs sur la carte');
    
    if (!map.current) {
      logger.warn('Impossible de rafraîchir la carte: carte non initialisée');
      return;
    }
    
    try {
      // Analyser les marqueurs dans l'état pour le débogage
      if (state.markers && state.markers.length > 0) {
        const userIds = state.markers.map(m => m.user_id).filter(id => id !== null && id !== undefined) as string[];
        
        // Utiliser une approche compatible avec les versions plus anciennes de JavaScript
        const uniqueUserIdsSet = new Set<string>();
        userIds.forEach(id => uniqueUserIdsSet.add(id));
        // const uniqueUserIds = Array.from(uniqueUserIdsSet); // Unused for now
      }
      
      // Calculer les marqueurs visibles
      const { visibleMarkerIds } = calculateVisibleMarkers();

      // Optimisation: Créer un Set pour des recherches plus rapides
      const visibleMarkerIdSet = new Set(visibleMarkerIds);
      const excludedCategoryIdSet = new Set(excludedCategoryIds);

      // Mettre à jour la visibilité de chaque marqueur
      Object.entries(markerElementsRef.current).forEach(([markerId, marker]) => {
        const categoryId = markersMap[markerId]?.categoryId || '';
        const isVisible = visibleMarkerIdSet.has(markerId) && !excludedCategoryIdSet.has(categoryId);
        
        updateMarkerVisibility(marker, isVisible);
      });

      // 1. Supprimer les marqueurs qui ne sont plus visibles
      const currentMarkerIds = Object.keys(markerElementsRef.current);
      const markersToRemove = currentMarkerIds.filter(id => !visibleMarkerIdSet.has(id));
      
      logger.debug(`Suppression de ${markersToRemove.length} marqueurs qui ne sont plus visibles`);
      
      markersToRemove.forEach(markerId => {
        try {
          const marker = markerElementsRef.current[markerId];
          if (marker) {
            logger.debug(`Suppression du marqueur ${markerId}`);
            marker.remove();
            delete markerElementsRef.current[markerId];
            
            // Nettoyer également le popup associé s'il existe
            if (popupRootRef.current[markerId]) {
              delete popupRootRef.current[markerId];
            }
          }
        } catch (err) {
          logger.error(`Erreur lors de la suppression du marqueur ${markerId}:`, err);
        }
      });
      
      // 2. Ajouter ou mettre à jour les marqueurs visibles
      logger.debug(`Traitement de ${visibleMarkers.length} marqueurs visibles`);
      
      visibleMarkers.forEach(markerData => {
        const category = markerData.categoryId ? categoriesMap[markerData.categoryId] : undefined;
        if (!category) {
          logger.warn(`Catégorie non trouvée pour le marqueur ${markerData.id}, catégorie ${markerData.categoryId}`);
          return;
        }
        
        const existingMarker = markerElementsRef.current[markerData.id];
        
        // Si le marqueur existe déjà, mettre à jour sa visibilité
        if (existingMarker) {
          // Mettre à jour la visibilité
          updateMarkerVisibility(existingMarker, true);
          
          // Vérifier si les coordonnées ont changé
          const currentLngLat = existingMarker.getLngLat();
          const shouldUpdatePosition = 
            currentLngLat.lng !== markerData.coordinates[0] || 
            currentLngLat.lat !== markerData.coordinates[1];
          
          if (shouldUpdatePosition) {
            logger.debug(`Mise à jour de la position du marqueur ${markerData.id}`);
            existingMarker.setLngLat(markerData.coordinates);
          }
          
          // Pas besoin de recréer le marqueur complet, juste mettre à jour sa position si nécessaire
        } else {
          // Créer un nouveau marqueur s'il n'existe pas encore
          logger.debug(`Création d'un nouveau marqueur ${markerData.id} avec catégorie ${category.name}`);
          createOrUpdateMarker(markerData, category);
        }
      });
      
      // Log final après mise à jour
      setTimeout(() => {
        logger.info(`Rafraîchissement optimisé terminé:`, {
          markersRemoved: markersToRemove.length,
          totalMarkersNow: Object.keys(markerElementsRef.current).length,
          visibleMarkersCount: visibleMarkers.length
        });
      }, 200);
    } catch (error) {
      logger.error('Erreur lors du rafraîchissement des marqueurs:', error);
    }
  }, [map, state.markers, state.visibleCategories, categoriesMap, updateMarkerVisibility, createOrUpdateMarker, calculateVisibleMarkers]);

  // Fonction pour ajuster la vue de la carte pour montrer tous les marqueurs visibles
  const fitMapToMarkers = useCallback(() => {
    logger.debug('Ajustement de la vue pour afficher tous les marqueurs visibles');
    
    if (!map.current) {
      logger.warn('Impossible d\'ajuster la vue: carte non initialisée');
      return;
    }
    
    try {
      // Récupérer tous les marqueurs visibles
      const visibleMarkerIds = Object.keys(markerElementsRef.current).filter(id => {
        const marker = markerElementsRef.current[id];
        const element = marker.getElement();
        return element.style.display !== 'none';
      });
      
      logger.debug(`Ajustement de la vue pour ${visibleMarkerIds.length} marqueurs visibles`);
      
      if (visibleMarkerIds.length === 0) {
        logger.info('Aucun marqueur visible à afficher');
        return;
      }
      
      // Créer un objet bounds pour calculer la zone englobant tous les marqueurs
      const bounds = new mapboxgl.LngLatBounds();
      
      // Ajouter les coordonnées de chaque marqueur visible aux bounds
      visibleMarkerIds.forEach(id => {
        const marker = markerElementsRef.current[id];
        bounds.extend(marker.getLngLat());
      });
      
      // Ajuster la vue de la carte pour montrer tous les marqueurs avec un padding
      map.current.fitBounds(bounds, {
        padding: 50, // Padding autour des marqueurs (en pixels)
        maxZoom: 16, // Zoom maximum pour éviter de trop zoomer sur un seul marqueur
        duration: 1000 // Durée de l'animation en millisecondes
      });
      
      logger.info('Vue ajustée pour afficher tous les marqueurs visibles');
    } catch (error) {
      logger.error('Erreur lors de l\'ajustement de la vue:', error);
    }
  }, [map]);

  // S'abonner à l'événement REFRESH_MAP_MARKERS
  useEffect(() => {
    // Ne s'abonner que si la carte est initialisée
    if (!map.current) return;

    logger.debug('Abonnement à l\'événement REFRESH_MAP_MARKERS');
    
    const unsubscribe = eventsService.subscribe('REFRESH_MAP_MARKERS', (data) => {
      logger.debug('Événement REFRESH_MAP_MARKERS reçu', data);
      
      // Si nous avons les informations spécifiques sur le marqueur à rafraîchir
      if (data && data.markerId && data.newCategoryId) {
        // Vérifier si le marqueur existe dans notre référence
        const existingMarker = markerElementsRef.current[data.markerId];
        let markerLngLat: mapboxgl.LngLat | undefined;
        
        if (existingMarker) {
          // Sauvegarder la position du marqueur avant le rafraîchissement
          markerLngLat = existingMarker.getLngLat();
          logger.debug('Position du marqueur avant rafraîchissement', { 
            markerId: data.markerId,
            lng: markerLngLat.lng,
            lat: markerLngLat.lat
          });
        } else {
          logger.warn(`Marqueur ${data.markerId} non trouvé avant le rafraîchissement`);
        }
        
        // Récupérer l'état actuel de la carte
        let currentCenter: mapboxgl.LngLat | undefined;
        let currentZoom: number | undefined;
        
        if (map.current) {
          currentCenter = map.current.getCenter();
          currentZoom = map.current.getZoom();
          logger.debug('État actuel de la carte', { 
            center: { lng: currentCenter.lng, lat: currentCenter.lat }, 
            zoom: currentZoom 
          });
        }
        
        // Rafraîchir uniquement le marqueur concerné
        logger.debug(`Début du rafraîchissement du marqueur ${data.markerId}`);
        refreshSingleMarker(data.markerId, data.newCategoryId);
        logger.debug(`Fin du rafraîchissement du marqueur ${data.markerId}`);
        
        // Si preserveZoom est activé et que nous avons la position du marqueur
        if (data.preserveZoom && map.current) {
          // Attendre un court instant pour que le marqueur soit rafraîchi
          setTimeout(() => {
            if (map.current) {
              // Récupérer le marqueur après rafraîchissement
              const refreshedMarker = markerElementsRef.current[data.markerId];
              
              if (refreshedMarker) {
                const newLngLat = refreshedMarker.getLngLat();
                logger.debug('Position du marqueur après rafraîchissement', { 
                  markerId: data.markerId,
                  lng: newLngLat.lng,
                  lat: newLngLat.lat
                });
                
                // Vérifier si le recentrage est désactivé (cas du glisser-déposer)
                if (!data.disableRecentering) {
                  // Centrer sur le marqueur tout en conservant le zoom actuel
                  map.current.jumpTo({
                    center: newLngLat,
                    zoom: currentZoom
                  });
                  logger.debug('Carte centrée sur le marqueur après rafraîchissement', { 
                    markerId: data.markerId,
                    zoom: currentZoom 
                  });
                } else {
                  // Conserver la position et le zoom actuels sans recentrage
                  logger.debug('Recentrage désactivé pour le glisser-déposer', {
                    markerId: data.markerId
                  });
                }
                
                // Animer le marqueur pour indiquer visuellement le changement de catégorie
                // mais sans déclencher de zoom supplémentaire
                try {
                  // Animer le marqueur sans zoom
                  const markerElement = refreshedMarker.getElement();
                  if (markerElement) {
                    // Animation simple sans zoom
                    markerElement.style.transition = 'transform 0.3s ease-in-out';
                    markerElement.style.transform = 'scale(1.3)';
                    
                    setTimeout(() => {
                      markerElement.style.transform = 'scale(1)';
                      setTimeout(() => {
                        markerElement.style.transition = '';
                      }, 300);
                    }, 300);
                  }
                } catch (error) {
                  logger.error('Erreur lors de l\'animation du marqueur', error);
                }
              } else {
                logger.warn(`Marqueur ${data.markerId} non trouvé après le rafraîchissement`);
                
                // Si le marqueur n'est pas trouvé et que le recentrage n'est pas désactivé,
                // restaurer simplement la position et le zoom précédents
                if (!data.disableRecentering && currentCenter && currentZoom) {
                  map.current.jumpTo({
                    center: currentCenter,
                    zoom: currentZoom
                  });
                  logger.debug('Position et zoom restaurés par défaut');
                }
              }
            }
          }, 100); // Augmenter le délai pour s'assurer que le marqueur est bien rafraîchi
        }
      } else {
        // Sinon, rafraîchir tous les marqueurs (cas de secours)
        refreshAllMarkers();
      }
    }, 'useMarkers'); // Identifiant unique pour ce composant
    
    // Se désabonner lors du démontage du composant
    return () => {
      logger.debug('Désabonnement de l\'événement REFRESH_MAP_MARKERS');
      unsubscribe();
    };
  }, [map, refreshAllMarkers, refreshSingleMarker]);

  // Forcer un rafraîchissement complet de la carte
  const forceMapRefresh = useCallback((excludedCategoryIds: string[] = []) => {
    logger.debug('Forçage du rafraîchissement complet de la carte');
    
    if (!map.current) {
      logger.warn('Impossible de rafraîchir la carte: carte non initialisée');
      return;
    }
    
    try {
      // Approche radicale: réinitialiser complètement la carte
      const currentCenter = map.current.getCenter();
      const currentZoom = map.current.getZoom();
      const currentBearing = map.current.getBearing();
      const currentPitch = map.current.getPitch();
      
      // Nettoyer tous les marqueurs
      clearAllMarkersFromMap();
      
      // Forcer un redessinage de la carte
      map.current.resize();
      
      // Restaurer la vue précédente
      map.current.jumpTo({
        center: currentCenter,
        zoom: currentZoom,
        bearing: currentBearing,
        pitch: currentPitch
      });
      
      // Recréer tous les marqueurs visibles, en excluant les catégories spécifiées
      const visibleCategoryIds = Array.from(state.visibleCategories).filter(
        id => !excludedCategoryIds.includes(id)
      );
      
      // Filtrer les marqueurs pour exclure ceux des catégories supprimées
      // Vérifier également que le marqueur existe toujours dans l'état
      const visibleMarkers = state.markers.filter(marker => 
        marker.categoryId && 
        visibleCategoryIds.includes(marker.categoryId) &&
        !excludedCategoryIds.includes(marker.categoryId)
      );
      
      logger.debug(`Recréation de ${visibleMarkers.length} marqueurs visibles (excluant ${excludedCategoryIds.length} catégories)`);
      
      // Recréer chaque marqueur visible
      visibleMarkers.forEach(markerData => {
        const category = markerData.categoryId ? categoriesMap[markerData.categoryId] : undefined;
        if (category) {
          createOrUpdateMarker(markerData, category);
        } else {
          logger.warn(`Catégorie non trouvée pour le marqueur ${markerData.id}, catégorie ${markerData.categoryId}`);
        }
      });
      
      logger.info(`Rafraîchissement complet de la carte terminé avec ${visibleMarkers.length} marqueurs`);
    } catch (error) {
      logger.error('Erreur lors du rafraîchissement complet de la carte:', error);
    }
  }, [map, state.markers, state.visibleCategories, categoriesMap, clearAllMarkersFromMap, createOrUpdateMarker]);

  // Mettre à jour le marker sélectionné quand le state change
  useEffect(() => {
    if (!selectedMarker) return;

    // Utiliser markersMap au lieu de find pour une meilleure performance
    const updatedMarker = markersMap[selectedMarker.id];
    
    if (updatedMarker && !isEqual(updatedMarker, selectedMarker)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Mise à jour du marker sélectionné', {
          old: {
            id: selectedMarker.id,
            categoryId: selectedMarker.categoryId,
            actionsCount: selectedMarker.actions?.length
          },
          new: {
            id: updatedMarker.id,
            categoryId: updatedMarker.categoryId,
            actionsCount: updatedMarker.actions?.length
          }
        });
      }
      
      // Cleanup implicite : React nettoiera automatiquement l'ancien state
      setSelectedMarker(updatedMarker);
    }
  }, [markersMap, selectedMarker]); // Utiliser markersMap au lieu de state.markers

  // Gérer le chargement initial de la carte
  useEffect(() => {
    // Early return si pas de carte ou déjà chargée
    if (!map.current || mapLoadedRef.current) return;

    const onMapLoad = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Carte chargée et prête');
      }
      mapLoadedRef.current = true;
      setIsMapReady(true); // Mettre à jour l'état pour indiquer que la carte est prête
    };

    // Si la carte est déjà chargée
    if (map.current.loaded()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Carte déjà chargée');
      }
      onMapLoad();
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('En attente du chargement de la carte...');
    }

    // Cleanup function pour éviter les memory leaks
    const mapInstance = map.current;
    mapInstance.on('load', onMapLoad);

    return () => {
      mapInstance.off('load', onMapLoad);
    };
  }, []); // Dépendance vide car map.current est une ref

  // Fonction pour récupérer les marqueurs depuis Supabase
  const fetchMarkers = useCallback(async () => {
    try {
      logger.info('Récupération des marqueurs depuis Supabase');
      
      // Récupérer les marqueurs depuis Supabase avec filtrage par utilisateur si nécessaire
      const markers = await markersService.getAll(selectedUserId);
      
      // Analyser les marqueurs récupérés pour le débogage
      if (markers && markers.length > 0) {
        const userIds = markers.map(m => m.user_id).filter(id => id !== null && id !== undefined) as string[];
        
        // Utiliser une approche compatible avec les versions plus anciennes de JavaScript
        const uniqueUserIdsSet = new Set<string>();
        userIds.forEach(id => uniqueUserIdsSet.add(id));
        // const uniqueUserIds = Array.from(uniqueUserIdsSet); // Unused for now
        
      }
      
      logger.debug(`${markers.length} marqueurs récupérés depuis Supabase${selectedUserId ? ` (filtrés pour l'utilisateur ${selectedUserId})` : ''}`);
      
      // Mettre à jour l'état avec les marqueurs récupérés
      dispatch({ type: 'SET_MARKERS', payload: markers });
      
      // Marquer le chargement initial comme terminé
      isInitialLoadDone.current = true;
      
      return markers;
    } catch (error) {
      logger.error('Erreur lors de la récupération des marqueurs:', error);
      return [];
    }
  }, [dispatch, selectedUserId]); // Ajouter selectedUserId aux dépendances

  // Charger les marqueurs depuis Supabase au démarrage
  useEffect(() => {
    const loadMarkersFromSupabase = async () => {
      try {
        logger.debug('Chargement des marqueurs depuis Supabase...');
        dispatch({ type: 'SET_LOADING', payload: true });
        
        await fetchMarkers();
        
        dispatch({ type: 'SET_LOADING', payload: false });
      } catch (error) {
        logger.error('Erreur lors du chargement des marqueurs:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Erreur lors du chargement des marqueurs' });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    
    // Charger les marqueurs seulement si ce n'est pas déjà fait
    if (!isInitialLoadDone.current && state.markers.length === 0) {
      loadMarkersFromSupabase();
    }
  }, [fetchMarkers]); // Dépend de fetchMarkers qui inclut selectedUserId
  
  // Effet pour recharger les marqueurs quand l'utilisateur sélectionné change
  useEffect(() => {
    // Ne pas recharger lors du premier rendu
    if (isInitialLoadDone.current) {
      logger.info(`Rechargement des marqueurs suite à un changement d'utilisateur sélectionné: ${selectedUserId || 'tous les utilisateurs'}`);
      fetchMarkers();
    }
  }, [selectedUserId, fetchMarkers]);

  // Effet pour créer les marqueurs sur la carte une fois que la carte est prête et les marqueurs chargés
  useEffect(() => {
    // Si la carte n'est pas prête ou s'il n'y a pas de marqueurs, on ne fait rien
    if (!isMapReady || state.markers.length === 0 || !map.current) {
      return;
    }

    logger.info(`Création de ${state.markers.length} marqueurs sur la carte maintenant que la carte est prête`);
    
    // Récupérer les catégories visibles
    const visibleCategoryIds = Array.from(state.visibleCategories);
    
    // Filtrer les marqueurs pour n'afficher que ceux des catégories visibles
    const visibleMarkers = state.markers.filter(marker => 
      marker.categoryId && visibleCategoryIds.includes(marker.categoryId)
    );
    
    logger.debug(`Création de ${visibleMarkers.length} marqueurs visibles sur la carte`);
    
    // Nettoyer tous les marqueurs existants
    clearAllMarkersFromMap();
    
    // Créer chaque marqueur visible
    visibleMarkers.forEach(markerData => {
      const category = markerData.categoryId ? categoriesMap[markerData.categoryId] : undefined;
      if (category) {
        createOrUpdateMarker(markerData, category);
      } else {
        logger.warn(`Catégorie non trouvée pour le marqueur ${markerData.id}, catégorie ${markerData.categoryId}`);
      }
    });
    
    logger.info(`Création des marqueurs sur la carte terminée`);
    
    // Émettre un événement pour informer les autres composants que les marqueurs ont été créés
    // eventsService.emit('MARKERS_CREATED', {
    //   totalMarkers: visibleMarkers.length,
    //   timestamp: new Date().toISOString()
    // });
    
    // DÉSACTIVÉ TEMPORAIREMENT: Émission de REFRESH_MAP_MARKERS qui causait un cycle infini
    // eventsService.emit('REFRESH_MAP_MARKERS', { 
    //   totalMarkers: visibleMarkers.length,
    //   timestamp: new Date().toISOString()
    // });
    
  }, [isMapReady, state.markers, state.visibleCategories, categoriesMap, clearAllMarkersFromMap, createOrUpdateMarker]);

  useEffect(() => {
    logger.debug('État de markersMap:', {
      totalMarkers: Object.keys(markersMap).length,
      sampleMarker: Object.values(markersMap)[0]
    });
  }, [markersMap]);

  useEffect(() => {
    logger.debug('État de categoriesMap:', {
      totalCategories: Object.keys(categoriesMap).length,
      categories: Object.values(categoriesMap).map(cat => ({ id: cat.id, name: cat.name }))
    });
  }, [categoriesMap]);

  useEffect(() => {
    logger.debug('État de markersByCategoryId:', {
      totalCategories: Object.keys(markersByCategoryId).length,
      markersPerCategory: Object.entries(markersByCategoryId).map(([catId, markers]) => ({
        categoryId: catId,
        markerCount: markers.size,
        categoryName: categoriesMap[catId]?.name
      }))
    });
  }, [markersByCategoryId, categoriesMap]);

  // Effet pour mettre à jour les marqueurs lorsque les catégories visibles changent ou le filtre notifications
  useEffect(() => {
    if (map.current && mapLoadedRef.current) {
      refreshAllMarkers();
    }
  }, [map, refreshAllMarkers, state.visibleCategories, state.showOnlyMarkersWithNotifications]);

  // Référence pour le debounce du rafraîchissement des marqueurs
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Fonction pour rafraîchir les marqueurs avec debounce
  const debouncedRefreshMarkers = useCallback(() => {
    // Annuler le timer précédent s'il existe
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    // Créer un nouveau timer
    refreshTimerRef.current = setTimeout(() => {
      if (map.current && mapLoadedRef.current) {
        clearAllMarkersFromMap();
        refreshAllMarkers();
      }
      refreshTimerRef.current = null;
    }, 100); // Délai de debounce de 100ms
  }, [map, clearAllMarkersFromMap, refreshAllMarkers]);
  
  // Effet pour rafraîchir les marqueurs lorsque l'utilisateur sélectionné change
  useEffect(() => {
    if (map.current && mapLoadedRef.current) {
      logger.info(`Utilisateur sélectionné changé: ${selectedUserId}, rafraîchissement des marqueurs`);
      // Utiliser la fonction debounced pour éviter les rafraîchissements trop fréquents
      debouncedRefreshMarkers();
    }
  }, [selectedUserId, map, debouncedRefreshMarkers]);

  // Mettre à jour la visibilité des marqueurs lorsque les catégories visibles changent
  useEffect(() => {
    // Ne rien faire si la carte n'est pas initialisée
    if (!map.current) return;

    logger.debug('Mise à jour de la visibilité des marqueurs suite à un changement des catégories visibles');
    
    // Parcourir tous les marqueurs existants et mettre à jour leur visibilité
    Object.entries(markerElementsRef.current).forEach(([markerId, marker]) => {
      // Trouver les données du marqueur
      const markerData = state.markers.find(m => m.id === markerId);
      if (!markerData) {
        logger.warn(`Marqueur ${markerId} non trouvé dans l'état, impossible de mettre à jour sa visibilité`);
        return;
      }
      
      // Déterminer si le marqueur doit être visible
      const shouldBeVisible = state.visibleCategories.has(markerData.categoryId || '');
      
      // Mettre à jour la visibilité du marqueur
      updateMarkerVisibility(marker, shouldBeVisible);
    });
    
    logger.info(`Visibilité des marqueurs mise à jour (${state.visibleCategories.size} catégories visibles)`);
  }, [map, state.visibleCategories, state.markers, updateMarkerVisibility]);

  // Gérer les animations pendant le déplacement de la carte
  useEffect(() => {
    if (!map.current) return;

    const handleMoveStart = () => {
      isMovingRef.current = true;
      requestAnimationFrame(() => {
        // Utiliser requestAnimationFrame pour de meilleures performances
        Object.values(markerElementsRef.current).forEach(marker => {
          const element = marker.getElement();
          element.style.transition = 'none';
          element.style.opacity = '0.6';
        });
      });
    };

    const handleMoveEnd = () => {
      isMovingRef.current = false;
      requestAnimationFrame(() => {
        Object.values(markerElementsRef.current).forEach(marker => {
          const element = marker.getElement();
          element.style.transition = 'opacity 0.2s ease-in-out';
          element.style.opacity = '1';
        });
      });
    };

    // Stocker l'instance de la carte pour le cleanup
    const mapInstance = map.current;
    mapInstance.on('movestart', handleMoveStart);
    mapInstance.on('moveend', handleMoveEnd);

    return () => {
      // Cleanup des événements
      mapInstance.off('movestart', handleMoveStart);
      mapInstance.off('moveend', handleMoveEnd);
      // Réinitialiser l'état de déplacement
      isMovingRef.current = false;
    };
  }, []); // Dépendance vide car on utilise des refs

  // Écouter l'événement pour ouvrir le modal d'un marker depuis une notification
  useEffect(() => {
    const handleOpenMarkerModal = (data: { markerId: string, notificationId?: string, notificationTitle?: string, actionId?: string }) => {
      logger.debug('Événement OPEN_MARKER_MODAL reçu:', data);
      
      if (!data.markerId) {
        logger.error('Aucun markerId fourni dans l\'événement OPEN_MARKER_MODAL');
        return;
      }
      
      // Rechercher le marker dans l'état
      const marker = state.markers.find(m => m.id === data.markerId);
      
      if (marker) {
        logger.debug('Marker trouvé, ouverture du modal:', marker);
        
        // Rechercher le marker sur la carte pour l'animation
        const mapboxMarker = markerElementsRef.current[marker.id];
        
        if (mapboxMarker && map.current) {
          // Récupérer les coordonnées du marker
          const lngLat = mapboxMarker.getLngLat();
          
          // Animer la carte pour zoomer sur le marker
          map.current.flyTo({
            center: lngLat,
            zoom: Math.min(map.current.getZoom() + 1, 16), // Zoom un peu plus, mais pas trop
            duration: 1000, // Durée de l'animation en ms
            essential: true // Cette animation est considérée comme essentielle
          });
          
          // Ajouter un effet de pulsation au marker
          const markerElement = mapboxMarker.getElement();
          markerElement.classList.add('marker-pulse');
          
          // Retirer l'effet après l'animation
          setTimeout(() => {
            markerElement.classList.remove('marker-pulse');
            
            // Ouvrir le modal après l'animation
            handleManageMarker(marker, {
              notificationId: data.notificationId,
              notificationTitle: data.notificationTitle,
              actionId: data.actionId
            });
          }, 1200); // Un peu plus que la durée de l'animation pour être sûr
        } else {
          // Si le marker n'est pas sur la carte, ouvrir directement le modal
          handleManageMarker(marker, {
            notificationId: data.notificationId,
            notificationTitle: data.notificationTitle,
            actionId: data.actionId
          });
        }
      } else {
        logger.error('Marker non trouvé dans l\'état:', data.markerId);
        
        // Tenter de récupérer le marker depuis Supabase
        (async () => {
          try {
            const markerFromDB = await markersService.getById(data.markerId);
            if (markerFromDB) {
              logger.debug('Marker récupéré depuis Supabase:', markerFromDB);
              
              // Mettre à jour l'état avec le marqueur récupéré
              dispatch({ type: 'ADD_MARKER', payload: markerFromDB });
              
              // Ouvrir le modal avec les données récupérées
              handleManageMarker(markerFromDB, {
                notificationId: data.notificationId,
                notificationTitle: data.notificationTitle
              });
            } else {
              logger.error('Marker introuvable dans Supabase');
            }
          } catch (error) {
            logger.error('Erreur lors de la récupération du marker depuis Supabase:', error);
          }
        })();
        
        return;
      }
    };
    
    const unsubscribe = eventsService.subscribe('OPEN_MARKER_MODAL', handleOpenMarkerModal, 'markers-hook');
    
    return () => {
      unsubscribe();
    };
  }, [state.markers, handleManageMarker, map]);


  // Écouter les événements d'animation et d'ouverture de popup des marqueurs
  useEffect(() => {
    if (!map.current) return;
    
    // Si c'est un appareil mobile, ne pas s'abonner aux événements d'animation
    if (isMobile) {
      logger.debug('Appareil mobile détecté, animations désactivées pour de meilleures performances');
      return;
    }
    
    const handleAnimateMarker = (data: { markerId: string }) => {
      if (!data.markerId || !map.current) return;
      
      // Vérifier si un glisser-déposer est en cours (variable globale définie dans Sidebar.tsx)
      const isDragging = (window as any).isDraggingState === true;
      
      const marker = markerElementsRef.current[data.markerId];
      if (marker) {
        // Si un glisser-déposer est en cours, ne pas zoomer ni centrer
        if (isDragging) {
          logger.debug(`Animation du marqueur ${data.markerId} sans zoom ni centrage (glisser-déposer en cours)`);
          
          // Animer uniquement le marqueur sans modifier la carte
          try {
            const markerElement = marker.getElement();
            if (markerElement) {
              // Animation simple sans zoom
              markerElement.style.transition = 'transform 0.3s ease-in-out';
              markerElement.style.transform = 'scale(1.3)';
              
              setTimeout(() => {
                markerElement.style.transform = 'scale(1)';
                setTimeout(() => {
                  markerElement.style.transition = '';
                }, 300);
              }, 300);
            }
          } catch (error) {
            logger.error('Erreur lors de l\'animation du marqueur', error);
          }
          return;
        }
        
        // Comportement normal si ce n'est pas un glisser-déposer
        const lngLat = marker.getLngLat();
        // Effet de zoom minimal (10% de l'effet original)
        const currentZoom = map.current.getZoom();
        
        // Réduire le zoom minimum à 13 au lieu de 14
        const targetZoom = Math.max(currentZoom, 13);
        
        // Si on est déjà au niveau de zoom cible ou supérieur, ne pas zoomer davantage
        if (currentZoom >= targetZoom - 1) { // Seuil réduit pour éviter le zoom dans plus de cas
          // Juste centrer la carte sans zoomer
          map.current.flyTo({
            center: lngLat,
            duration: 1200, // Animation lente
            essential: true,
            speed: 0.4 // Vitesse encore plus réduite
          });
        } else {
          // Zoomer mais seulement de 10% de la différence
          const zoomDifference = targetZoom - currentZoom;
          const newZoom = currentZoom + (zoomDifference * 0.1);
          
          map.current.flyTo({
            center: lngLat,
            zoom: newZoom,
            duration: 1200, // Animation lente
            essential: true,
            speed: 0.4 // Vitesse encore plus réduite
          });
        }
      }
    };
    
    const handleOpenMarkerPopup = (data: { markerId: string }) => {
      if (!data.markerId || !map.current) return;
      
      // Récupérer le marqueur et ses données
      const marker = markerElementsRef.current[data.markerId];
      const markerData = markersMap[data.markerId];
      
      if (!marker || !markerData) {
        logger.error(`Marqueur ${data.markerId} non trouvé pour l'ouverture du popup`);
        return;
      }
      
      // Récupérer la catégorie du marqueur
      const category = markerData.categoryId ? categoriesMap[markerData.categoryId] : undefined;
      
      // Ouvrir le popup du marqueur
      logger.debug(`Ouverture du popup pour le marqueur ${data.markerId} depuis la sidebar`);
      
      // Créer un événement de clic factice pour le passer à handleMarkerClick
      const fakeEvent = {
        preventDefault: () => {},
        stopPropagation: () => {}
      } as unknown as MouseEvent;
      
      handleMarkerClick(fakeEvent, marker, markerData, category);
    };
    
    // S'abonner aux événements
    const unsubscribeAnimate = eventsService.subscribe('ANIMATE_MARKER' as any, handleAnimateMarker, 'map-markers');
    const unsubscribeOpenPopup = eventsService.subscribe('OPEN_MARKER_POPUP' as any, handleOpenMarkerPopup, 'map-markers');
    
    return () => {
      unsubscribeAnimate();
      unsubscribeOpenPopup();
    };
  }, [map, markersMap, categoriesMap, handleMarkerClick, isMobile]);

  return {
    markerElementsRef,
    visibleMarkers,
    createMarker,
    updateMarker,
    deleteMarker,
    handleManageMarker,
    handleSaveMarker,
    isEditModalOpen,
    setIsEditModalOpen,
    selectedMarker,
    setSelectedMarker,
    addMarker,
    openMarkerPopup,
    handleCloseEditModal,
    clearAllMarkersFromMap,
    refreshAllMarkers,
    forceMapRefresh,
    fitMapToMarkers
  };
};

